import React, { FC, forwardRef, ReactNode, useState } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import FeatherIcon from "feather-icons-react";

import { Chip } from "@components/common";
import { Box, Stack, Typography } from "@components/base";


interface Option {
  id: string;
  title: string;
  type: string;
  icon?: ReactNode;
}

interface ChipSelect {
  id: string;
  value: string[] | string;
  onChange: (value: string[] | string) => void;
  options: Option[];
  singleChipType?: string;
  placeholder?: string;
}

const ChipSelect: FC<ChipSelect> = forwardRef(
  ({ value, onChange, options, id, singleChipType, placeholder }, ref) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLElement>): void => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = (): void => {
      setAnchorEl(null);
    };

    const handleToggleOption = (id: string): void => {
      if (isValueArray) {
        const newValue = value.includes(id)
          ? value.filter((v) => v !== id)
          : [...value, id];

        onChange(newValue);
      } else {
        onChange(id);
      }

      setAnchorEl(null);
    };

    const isValueArray = Array.isArray(value);
    const isVisiblePlusButton =
      (value.length !== options.length && isValueArray) ||
      (!isValueArray && value.length === 0);
    const isVisibleSingleOptions = !isValueArray && value.length > 0;
    const isVisibleMultipleOptions = isValueArray && value.length > 0;

    const filteredOptions = isValueArray
      ? options.filter((o) => !value.includes(o.id))
      : options.filter((o) => value !== o.id);

    return (
      <>
        <Stack
          ref={ref as React.RefObject<HTMLDivElement>}
          alignItems="center"
          minHeight="36px"
          spacing={1}
          direction="row"
          flexWrap="wrap"
        >
          <Stack direction="row" key={id} flexWrap="wrap" gap={1}>
            {isVisibleMultipleOptions &&
              value.map((id) => (
                <Box key={id}>
                  <Chip
                    type={options.find((option) => option.id === id)?.type ?? ""}
                    label={options.find((option) => option.id === id)?.title}
                    onDelete={() => handleToggleOption(id)}
                  />
                </Box>
              ))}
            {isVisibleSingleOptions && (
              <Chip
                onClick={(e) => handleOpen(e)}
                type={singleChipType ?? ""}
                size="medium"
                label={
                  <Box alignItems={"center"} display={"flex"} gap={1}>
                    {options.find((o) => o.id === value)?.icon}
                    {options.find((o) => o.id === value)?.title}
                  </Box>
                }
              />
            )}
            {isVisiblePlusButton && (
              <IconButton
                aria-label="plus"
                onClick={handleOpen}
                id={id}
                color="primary"
              >
                <FeatherIcon icon="plus" size={20} />
              </IconButton>
            )}
          </Stack>
        </Stack>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          {placeholder && (
            <MenuItem disabled>
              <Typography variant="body2" color="text">
                {placeholder}
              </Typography>
            </MenuItem>
          )}
          {filteredOptions.map((option) => (
            <MenuItem key={option.id} onClick={() => handleToggleOption(option.id)}>
              <Chip
                type={singleChipType || option.type}
                label={
                  <Box alignItems={"center"} display={"flex"} gap={1}>
                    {option?.icon}
                    {option.title}
                  </Box>
                }
              />
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  },
);

export default ChipSelect;
